// @ts-strict-ignore
import React, { useCallback, useMemo, useRef } from 'react';
import type { Option } from '@usealloy/component-library';
import { MultiSelect } from '@usealloy/component-library';

import { ActionType } from 'flow/store/engine/constants';
import { populateDataRouteData } from 'flow/service/dataRoutes/dataRoutes';
import type { ExtendedDataRoute } from 'flow/store/engine/types';
import { generatePublicId } from 'flow/service/publicIds';

import { createDataRoute, updateAction, updateDataRouteValue } from 'flow/store/redux/slice';
import {
  selectFocusedAction,
  selectNodeLineageForAnchorNode,
  selectWorkflowData,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { isActionOfType } from '~/dashboard/views/workflows/service/actions';

import {
  getLinkableActionDataType,
  getAxisDataRouteKey,
  getAxisHeaderActionOptions,
  getAxisHeaderAttributeOptions,
  getMatrixAxisByActionDataType,
  getMultiSelectProps,
  transformActionToOption,
  updateMatrixModelWithDataTypeChange,
  isAxisDataTypeDifferent,
  filterAxisOptions,
} from './helpers';

import ActionOption from './components/Option';
import SingleValue from './components/SingleValue';

import type {
  AxisHeaderDropdownProps, AxisHeaderActionOption, AxisHeaderAttributeOption,
} from './types';


export default function AxisHeaderDropdown({
  axis, isDisabled = false, action,
}: AxisHeaderDropdownProps) {
  const dispatch = useAppDispatch();
  const { actions, dataRoutes } = useAppSelector(selectWorkflowData);
  const { workflow, services } = useAppSelector(selectWorkflowProps);
  const nodeLineage = useAppSelector(selectNodeLineageForAnchorNode);
  const focusedAction = useAppSelector(selectFocusedAction);

  // Use the provided action prop if available, otherwise fall back to focused action
  const sourceAction = action || focusedAction;
  const { publicId, matrixModel } = sourceAction;

  const axisHeaderActionOptions: AxisHeaderActionOption[] = useMemo(
    () => getAxisHeaderActionOptions({
      actions, focusedPublicId: publicId, nodeLineage,
    }),
    [actions, nodeLineage, publicId]);

  const axisDataRouteKey = getAxisDataRouteKey(axis);
  const dataRouteId = matrixModel[axisDataRouteKey];
  const dataRoute = dataRoutes[dataRouteId];
  const targetAction = actions[dataRoute?.sourceId];

  const extendedDataRoute: ExtendedDataRoute = useMemo(() => targetAction
    && targetAction.type === ActionType.SERVICE
    && populateDataRouteData({ actions, dataRoute, services }),
  [actions, dataRoute, services, targetAction]);

  const attributeOptions: Option[] = useMemo(() => (extendedDataRoute)
    && getAxisHeaderAttributeOptions({ dataRoute: extendedDataRoute, services, workflow }),
  [extendedDataRoute, services, workflow]);

  const selectedActionOption: AxisHeaderActionOption = useMemo(
    () => (targetAction ? transformActionToOption(targetAction) : null), [targetAction],
  );

  const selectedServiceAttributeOption = extendedDataRoute && {
    label: extendedDataRoute.attribute.displayName,
    value: `${extendedDataRoute.attribute.id}`,
  };

  const handleActionAxisOnChange = useCallback((actionOption: AxisHeaderActionOption) => {
    const actionToLink = actions[actionOption.value];
    let updateMatrixModelAxis = false;
    const updatedMatrixModel = { ...matrixModel };

    // if axis has no selected action:
    if (!dataRoute) {
      if (!isActionOfType(actionToLink, ActionType.SERVICE)) {
        updateMatrixModelAxis = true;
      }
      const newDataRoutePublicId = generatePublicId('dataroute');
      dispatch(createDataRoute({
        path: newDataRoutePublicId,
        value: {
          publicId: newDataRoutePublicId,
          sourceId: actionOption.value,
          targetId: publicId,
        },
        expressionPath: '',
      }));

      dispatch(updateAction({
        path: `${publicId}.matrixModel.${axisDataRouteKey}`,
        value: newDataRoutePublicId,
      }));

      updatedMatrixModel[axisDataRouteKey] = newDataRoutePublicId;
    }

    // if changing axis to a different action:
    if (dataRoute && actionOption.value !== dataRoute.sourceId) {
      if (!isActionOfType(actionToLink, ActionType.SERVICE)) {
        updateMatrixModelAxis = true;
      }
      dispatch(updateDataRouteValue({
        path: `${dataRoute.publicId}.sourceId`,
        value: actionOption.value,
      }));
    }

    // need to update matrix model based on selected action data type
    // service types are handled in handleOnAttributeChange
    if (updateMatrixModelAxis) {
      const dataType = getLinkableActionDataType(actionToLink);
      const originalAxisSettings = matrixModel.axis[axis.toLowerCase()];
      const newAxisSettings = getMatrixAxisByActionDataType(dataType);

      if (dataType
        && isAxisDataTypeDifferent(originalAxisSettings, newAxisSettings)
      ) {
        const updatedMatrixModelWithAxis = updateMatrixModelWithDataTypeChange({
          prev: originalAxisSettings,
          next: newAxisSettings,
          axis,
          matrixModel: updatedMatrixModel,
        });

        dispatch(updateAction({
          path: `${publicId}.matrixModel`,
          value: updatedMatrixModelWithAxis,
        }));
      }
    }
  }, [
    actions, axis, axisDataRouteKey, dataRoute, dispatch, matrixModel, publicId,
  ]);

  const handleOnAttributeChange = useCallback((
    attributeOption: AxisHeaderAttributeOption,
  ) => {
    if (+attributeOption.value !== +dataRoute.attribute.id) {
      dispatch(updateDataRouteValue({
        path: `${dataRoute.publicId}.attribute.id`,
        value: Number(attributeOption.value),
      }));

      const { dataType } = attributeOption.meta;
      const originalAxisSettings = matrixModel.axis[axis.toLowerCase()];
      const newAxisSettings = getMatrixAxisByActionDataType(dataType);

      if (isAxisDataTypeDifferent(originalAxisSettings, newAxisSettings)) {
        const updatedMatrixModelWithAxis = updateMatrixModelWithDataTypeChange({
          prev: originalAxisSettings,
          next: newAxisSettings,
          axis,
          matrixModel,
        });

        dispatch(updateAction({
          path: `${publicId}.matrixModel`,
          value: updatedMatrixModelWithAxis,
        }));
      }
    }
  }, [axis, dataRoute, dispatch, matrixModel, publicId]);

  const multiSelectRef = useRef(null);

  const placeholderText = `Select ${axis} Axis`;

  const autoFocus = (axis === 'X' && !dataRouteId)
    || (axis === 'Y' && !isDisabled && !dataRouteId);

  return (
    <>
      <MultiSelect
        {...getMultiSelectProps()}
        autoFocus={autoFocus}
        openMenuOnFocus
        closeMenuOnSelect
        filterOption={filterAxisOptions}
        onChange={handleActionAxisOnChange}
        options={axisHeaderActionOptions}
        selectedOptions={selectedActionOption}
        isSearchable
        isDisabled={isDisabled}
        placeholder={placeholderText}
        ref={multiSelectRef}
        components={{
          // @ts-expect-error TODO: fix this
          Option: ActionOption<AxisHeaderActionOption>,
          SingleValue,
        }}
      />
      {selectedActionOption?.meta.actionType === ActionType.SERVICE && (
        <MultiSelect
          {...getMultiSelectProps(selectedActionOption.meta.color)}
          closeMenuOnSelect
          onChange={handleOnAttributeChange}
          options={attributeOptions}
          selectedOptions={selectedServiceAttributeOption}
          isSearchable
          isDisabled={isDisabled}
          placeholder={placeholderText}
          ref={multiSelectRef}
        />
      )}
    </>
  );
}
